/**
 * App settings in browser storage, scoped to the person they belong to.
 *
 * `jarvis_settings` was a single global key while trips had already been
 * namespaced per owner (JAR-599). On a shared browser, user B signed in and
 * inherited user A's notification toggles, units and display preferences.
 *
 * Purge-on-signout sweeps every `jarvis_*` key, and a live session re-hydrates
 * the server-backed toggles on mount — so the exposure is the tab closed without
 * signing out, or a purge that did not run. Settings are preferences rather than
 * secrets, but "your app remembered someone else's choices" is a trust problem
 * whatever the data is, and a boundary that depends on a clean signout is not a
 * boundary on the machine where signouts do not happen.
 *
 * Namespacing makes it structural instead: a read for user B cannot address user
 * A's row at all.
 *
 * The shape mirrors localTrips deliberately — same prefix form, same
 * owner-as-parameter rule, same discard-not-adopt decision about the legacy key.
 * Two storage modules that disagree about who owns a key is how this gap opened.
 */

/** An owner id, or null for "nobody is signed in" — stated, never inferred. */
export type SettingsOwner = string | null;

const KEY_PREFIX = 'jarvis_settings:';

// The pre-namespace key. Discarded, never adopted — see discardLegacySettings.
const LEGACY_KEY = 'jarvis_settings';

const normalizeOwner = (owner: SettingsOwner): string | null =>
  owner && owner.trim() ? owner.trim() : null;

/** The key for this owner, or null when there is no owner to scope by. */
export function settingsStorageKey(owner: SettingsOwner): string | null {
  const id = normalizeOwner(owner);
  return id ? KEY_PREFIX + id : null;
}

/**
 * The stored settings for this owner, or null when there are none.
 *
 * Returns the raw parsed object; validating it against AppSettings stays with
 * the caller that knows that type. This module's job is whose row it is.
 */
export function readStoredSettings(owner: SettingsOwner): Record<string, unknown> | null {
  const key = settingsStorageKey(owner);
  // No owner means no settings — never a fallback to a shared key, or the leak
  // returns for every signed-out read.
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Persist this owner's settings. A write with no owner is dropped, not shared. */
export function writeStoredSettings(owner: SettingsOwner, settings: unknown): void {
  const key = settingsStorageKey(owner);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    /* ignore disabled or full storage */
  }
}

let legacyDiscarded = false;

/** Test seam, matching localTrips' __resetLegacyDiscard. The flag makes the
 *  legacy discard once-only, so without this the SECOND test to exercise it
 *  silently skips the migration it means to assert (JAR-1328). */
export function __resetLegacySettingsDiscard(): void {
  legacyDiscarded = false;
}

/**
 * Discard the pre-namespace settings. Once, and only when the app calls it —
 * never at module load.
 *
 * NOT adopted into the current owner's bucket, which was the other option and
 * the one JAR-834 asked for. Adopt-on-first-load hands whatever the last person
 * left behind to whoever signs in next, which is the exact leak this change
 * exists to close — it would narrow the bug to one deploy window rather than
 * remove it. localTrips made the same call for the same reason (JAR-599).
 *
 * The cost is real and worth stating: an existing user loses their locally
 * stored preferences once. The three notification toggles are server-backed and
 * re-hydrate on mount; the rest are one-tap preferences. That is a smaller
 * price than a settings row crossing between two people.
 *
 * Not run at import, because merely importing a module must not be destructive —
 * in a Node test, an SSR bundle, a worker, or an HMR reload.
 */
export function discardLegacySettings(): void {
  if (legacyDiscarded) return;
  legacyDiscarded = true;
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore disabled storage */
  }
}
